import React, { useState, useEffect } from 'react';

const Offeringarea = () => {
    // State to manage the expanded/collapsed state of each card
    const [websiteExpanded, setWebsiteExpanded] = useState(false);
    const [uiuxExpanded, setUiuxExpanded] = useState(false);
    const [digitalExpanded, setDigitalExpanded] = useState(false);
    const [dataExpanded, setDataExpanded] = useState(false);

    // CSS for the fade-in-up animation
    const animationStyle = `
        @keyframes fade-in-up {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        .animate-fade-in-up {
            animation: fade-in-up 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            opacity: 0;
        }
    `;

    // Hook to observe elements and apply animation
    useEffect(() => {
        const fadeElements = document.querySelectorAll('.fade-in-element');

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in-up');
                    observer.unobserve(entry.target);
                }
            });
        }, {
            threshold: 0.2 // Trigger when 20% of the element is visible
        });

        fadeElements.forEach((el, index) => {
            // Apply a staggered delay to each element
            el.style.animationDelay = `${index * 0.2}s`;
            observer.observe(el);
        });
    }, []);

    // Function to handle toggling the state for each card
    const toggleWebsite = () => setWebsiteExpanded(!websiteExpanded);
    const toggleUiux = () => setUiuxExpanded(!uiuxExpanded);
    const toggleDigital = () => setDigitalExpanded(!digitalExpanded);
    const toggleData = () => setDataExpanded(!dataExpanded);

    return (
        <div className="bg-gray-900 relative overflow-hidden font-[Inter] rounded-lg">
            <style>{animationStyle}</style>

            {/* Background Geometric Patterns */}
            {/* Left Side Hexagonal Pattern */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-64 h-96 opacity-10">
                <svg viewBox="0 0 200 300" className="w-full h-full">
                    {/* Hexagonal Grid Pattern */}
                    <g stroke="white" strokeWidth="1" fill="none">
                        <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" />
                        <polygon points="80,35 110,50 110,80 80,95 50,80 50,50" />
                        <polygon points="50,80 80,95 80,125 50,140 20,125 20,95" />
                        <polygon points="80,95 110,110 110,140 80,155 50,140 50,110" />
                        <polygon points="50,140 80,155 80,185 50,200 20,185 20,155" />
                        <polygon points="80,155 110,170 110,200 80,215 50,200 50,170" />
                    </g>
                </svg>
            </div>

            {/* Right Side Curved Lines */}
            <div className="absolute right-0 top-0 w-80 h-full opacity-15">
                <svg viewBox="0 0 300 600" className="w-full h-full" preserveAspectRatio="none">
                    <g stroke="white" strokeWidth="1" fill="none">
                        <path d="M250,0 Q200,50 250,100 Q300,150 250,200 Q200,250 250,300 Q300,350 250,400 Q200,450 250,500 Q300,550 250,600" />
                        <path d="M270,0 Q220,50 270,100 Q320,150 270,200 Q220,250 270,300 Q320,350 270,400 Q220,450 270,500 Q320,550 270,600" />
                        <path d="M290,0 Q240,50 290,100 Q340,150 290,200 Q240,250 290,300 Q340,350 290,400 Q240,450 290,500 Q340,550 290,600" />
                    </g>
                </svg>
            </div>

            {/* Main Content Container */}
            <div className="relative z-10 containers mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">

                {/* Header Section */}
                <div className="mb-16 flex flex-wrap items-center">
                    <div className="lg:w-1/2 w-full">
                        <div className="flex items-center mb-6 fade-in-element">
                            <div className="w-12 h-px bg-blue-500"></div>
                            <span className="mx-4 text-blue-400 font-medium text-sm uppercase tracking-wider">
                                What We're Offering
                            </span>
                            <div className="w-12 h-px bg-blue-500"></div>
                        </div>

                        {/* Main Heading */}
                        <h2 className="text-4xl lg:text-4xl xl:text-4xl font-bold text-white mb-8 leading-tight fade-in-element">
                            We Provide Exclusive Service<br />
                            For Your Business
                        </h2>
                    </div>

                    {/* Description Text */}
                    <div className="lg:w-1/2 w-full">
                        <p className="text-gray-400 text-lg leading-relaxed mr-8 lg:mr-32 fade-in-element">
                            In addition to our premium offerings, we also provide a wide range of specialized services designed to strengthen your business presence online and offline. These services include
                        </p>
                    </div>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">

                    {/* Website Development Card */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 hover:border-blue-500 transition-all duration-300 group fade-in-element">
                        {/* Icon */}
                        <div className="mb-6">
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg className="w-12 h-12 text-white group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                                    <circle cx="12" cy="12" r="2" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-4">Website Development</h3>
                        <p className={`text-gray-400 mb-6 text-sm leading-relaxed overflow-hidden transition-all duration-500 ease-in-out ${websiteExpanded ? 'max-h-96' : 'max-h-12'}`}>
                            From corporate websites to advanced web portals, we deliver engaging websites that represent your brand and convert visitors into customers.
                        </p>
                        <a href="#" className="inline-flex items-center text-white hover:text-blue-400 transition-colors group/link" onClick={(e) => { e.preventDefault(); toggleWebsite(); }}>
                            <span className="mr-2">{websiteExpanded ? 'Read Less' : 'Read More'}</span>
                            <svg className={`w-4 h-4 transform transition-transform ${websiteExpanded ? 'rotate-180' : 'group-hover/link:translate-x-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </a>
                    </div>

                    {/* UI/UX Designing Card */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 hover:border-blue-500 transition-all duration-300 group fade-in-element">
                        {/* Icon */}
                        <div className="mb-6">
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg className="w-12 h-12 text-white group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-4">UI/UX Designing</h3>
                        <p className={`text-gray-400 mb-6 text-sm leading-relaxed overflow-hidden transition-all duration-500 ease-in-out ${uiuxExpanded ? 'max-h-96' : 'max-h-12'}`}>
                            Detailed interface design services focused on creating seamless, user-friendly, and conversion-optimized experiences.
                        </p>
                        <a href="#" className="inline-flex items-center text-white hover:text-blue-400 transition-colors group/link" onClick={(e) => { e.preventDefault(); toggleUiux(); }}>
                            <span className="mr-2">{uiuxExpanded ? 'Read Less' : 'Read More'}</span>
                            <svg className={`w-4 h-4 transform transition-transform ${uiuxExpanded ? 'rotate-180' : 'group-hover/link:translate-x-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </a>
                    </div>

                    {/* Digital Marketing Card */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 hover:border-blue-500 transition-all duration-300 group fade-in-element">
                        {/* Icon */}
                        <div className="mb-6">
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg className="w-12 h-12 text-white group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-4">Delphi Development</h3>
                        <p className={`text-gray-400 mb-6 text-sm leading-relaxed overflow-hidden transition-all duration-500 ease-in-out ${digitalExpanded ? 'max-h-96' : 'max-h-12'}`}>
                            Build powerful, scalable, and high-performance desktop and enterprise applications with Delphi for cross-platform efficiency and robust solutions.
                        </p>
                        <a href="#" className="inline-flex items-center text-white hover:text-blue-400 transition-colors group/link" onClick={(e) => { e.preventDefault(); toggleDigital(); }}>
                            <span className="mr-2">{digitalExpanded ? 'Read Less' : 'Read More'}</span>
                            <svg className={`w-4 h-4 transform transition-transform ${digitalExpanded ? 'rotate-180' : 'group-hover/link:translate-x-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </a>
                    </div>

                    {/* Data Analysis Card */}
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 hover:border-blue-500 transition-all duration-300 group fade-in-element">
                        {/* Icon */}
                        <div className="mb-6">
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg className="w-12 h-12 text-white group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-4">Data Analysis</h3>
                        <p className={`text-gray-400 mb-6 text-sm leading-relaxed overflow-hidden transition-all duration-500 ease-in-out ${dataExpanded ? 'max-h-96' : 'max-h-12'}`}>
                            Unlock valuable insights from your data to optimize business operations and guide decision-making with confidence.
                        </p>
                        <a href="#" className="inline-flex items-center text-white hover:text-blue-400 transition-colors group/link" onClick={(e) => { e.preventDefault(); toggleData(); }}>
                            <span className="mr-2">{dataExpanded ? 'Read Less' : 'Read More'}</span>
                            <svg className={`w-4 h-4 transform transition-transform ${dataExpanded ? 'rotate-180' : 'group-hover/link:translate-x-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Offeringarea;
